/**
 * Media-Referenz-Auflösung für CMS-getriebene Sections.
 *
 * Section-JSONs (z.B. `home.hero`) enthalten Media-Referenzen als
 *   `{ path: "/media/homepage/xy.jpg", media_id?: "uuid" }`.
 *
 * `resolveMediaUrl(ref, fallback?)` gibt die beste verfügbare URL zurück:
 *   1. Wenn `media_id` gesetzt → `/api/media/{id}` (Backend redirect auf R2)
 *      → über Backend, damit Admin-Änderungen (neue Datei, Ersetzen) sofort
 *        durchschlagen ohne CDN-Cache-Busting.
 *   2. Wenn nur `path` → statischer Public-Pfad (Fallback für nicht migrierte
 *      Assets bzw. während der Migrations-Übergangszeit).
 *   3. Sonst → `fallback` (statischer Pfad aus Content-Lib) oder leerer String.
 *
 * Beide Formen sind akzeptiert:
 *   - String: als statischer Pfad behandelt.
 *   - Object: {path, media_id}.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL as string;

export type MediaRef =
    | string
    | { path?: string; media_id?: string }
    | null
    | undefined;

/** Direktlink auf den Media-Serve-Endpoint (302-Redirect zu R2 CDN). */
export function mediaUrl(mediaId: string): string {
    return `${BACKEND_URL}/api/media/${mediaId}`;
}

/**
 * IMMER Same-Origin-Proxy, egal was im `path`-Feld steht.
 *
 * WICHTIG: `mediaProxyUrl` unten bevorzugt `path` wenn es eine absolute HTTPS-
 * URL ist (z.B. direkte R2-CDN-URL) — das ist gut für <img>/<video>, aber
 * FATAL für den Photo Sphere Viewer, weil der Viewer bei Cross-Origin-Laden
 * ohne CORS-Header in den Error-State fällt (kein `Access-Control-Allow-Origin`
 * für die Preview-Domain).
 *
 * Diese Variante zwingt Same-Origin über `/api/media/{id}/proxy` — für
 * WebGL-Texture-Loader (Photo Sphere Viewer, Three.js) zwingend.
 */
export function mediaProxyUrlForced(mediaId: string): string {
    return `${BACKEND_URL}/api/media/${mediaId}/proxy`;
}

/**
 * Proxy-Variante: streamt via Backend statt 302-Redirect zu R2. Für
 * PhotoSphereViewer + andere Textur-Loader nötig, weil das Preview-Overlay
 * `window.fetch` mit `credentials:'include'` wrappt — R2 lehnt Cross-Origin
 * mit Credentials ohne passenden ACAO-Header ab. Der Proxy ist Same-Origin.
 * Funktioniert für Fotos UND Videos (Range-Requests werden durchgereicht).
 */
export function mediaProxyUrl(refOrId: MediaRef | string): string {
    if (!refOrId) return "";
    if (typeof refOrId === "string") {
        // Wenn's schon eine volle URL ist → unverändert (statischer Fallback).
        if (refOrId.startsWith("http") || refOrId.startsWith("/media/")) return refOrId;
        return `${BACKEND_URL}/api/media/${refOrId}/proxy`;
    }
    // Object: absolute URL im path → durchreichen (Direct-CDN bevorzugt).
    if (refOrId.path && (refOrId.path.startsWith("http://") || refOrId.path.startsWith("https://"))) {
        return refOrId.path;
    }
    if (refOrId.media_id) return `${BACKEND_URL}/api/media/${refOrId.media_id}/proxy`;
    return refOrId.path || "";
}

/** HLS-Playlist-URL. Endpoint gibt 404 wenn media.hls_ready=false — der
 *  Video360Viewer fängt das per Fallback zu MP4 (`src`-Prop) ab.
 *  Wenn im MediaRef.path bereits eine .m3u8-URL steht (direkt R2 CDN), wird
 *  diese bevorzugt — ohne Backend-Umweg. */
export function mediaHlsUrl(refOrId: MediaRef | string): string | undefined {
    if (!refOrId) return undefined;
    if (typeof refOrId !== "string" && refOrId.path && refOrId.path.endsWith(".m3u8")) {
        return refOrId.path;
    }
    const id = typeof refOrId === "string" ? refOrId : refOrId.media_id;
    if (!id) return undefined;
    return `${BACKEND_URL}/api/media/${id}/hls/index.m3u8`;
}

/**
 * Auflösung: media_id → API-URL, sonst path, sonst fallback.
 *
 * WICHTIG: Wenn ein `path` bereits eine absolute URL ist (`http://` oder
 * `https://`), wird DIESE bevorzugt zurückgegeben — auch wenn `media_id` gesetzt
 * ist. So kann das CMS gezielt auf eine spezifische CDN-URL (z.B. direkte R2-
 * URL, HLS-Playlist) zeigen, statt über den `/api/media/{id}`-Redirect zu gehen.
 * Nützlich für performance-kritische Assets und HLS-Manifeste.
 *
 * Achtung: liefert leeren String bei komplett fehlender Referenz — Aufrufer
 * sollte defensiv rendern (z.B. `img.src && <img ... />`).
 */
export function resolveMediaUrl(ref: MediaRef, fallback?: string): string {
    if (!ref) return fallback ?? "";
    if (typeof ref === "string") {
        // Absolute URL oder Static-Path unverändert durchreichen.
        return ref || fallback || "";
    }
    // Object mit path+media_id: absolute URL im `path` hat Vorrang.
    if (ref.path && (ref.path.startsWith("http://") || ref.path.startsWith("https://"))) {
        return ref.path;
    }
    if (ref.media_id) return mediaUrl(ref.media_id);
    if (ref.path) return ref.path;
    return fallback ?? "";
}
